import { API_BASE } from '../config/api';
import { tokenStore } from './tokenStore';

/** Tương thích kiểu GraphQLResult của @aws-amplify/api (service đọc .data). */
export interface GraphQLResult<T = any> {
  data?: T;
  errors?: { message: string }[];
}

interface GraphQLOptions {
  query: string;
  variables?: Record<string, any>;
  [key: string]: any;
}

// Lấy field selection đầu tiên: "... ) { getUsers(" -> "getUsers".
const extractOperation = (query: string): string => {
  const m = /\{\s*([A-Za-z_][A-Za-z0-9_]*)/.exec(query);
  if (!m) throw new Error('apiClient: cannot parse operation from query');
  return m[1];
};

const REFRESH_ENDPOINT = `${API_BASE}/auth/refresh`;

// Thử xoay access token bằng refresh token; true nếu thành công.
const tryRefresh = async (): Promise<boolean> => {
  const refresh = tokenStore.refresh;
  if (!refresh) return false;
  try {
    const res = await fetch(REFRESH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) return false;
    const body = await res.json();
    if (body?.access_token) {
      tokenStore.setAccess(body.access_token);
      if (body.refresh_token) localStorage.setItem('agentx.refresh_token', body.refresh_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const postOnce = async (op: string, variables: Record<string, any>): Promise<Response> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = tokenStore.access;
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_BASE}/api/v1/${op}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(variables ?? {}),
  });
};

/**
 * Client REST thay Amplify. Giữ đúng chữ ký `.graphql({query, variables})` và
 * shape trả về `{ data: { <op>: ... } }` để các service không phải đổi.
 */
const restClient = {
  async graphql<T = any>(options: GraphQLOptions): Promise<GraphQLResult<T>> {
    const op = extractOperation(options.query);
    let res = await postOnce(op, options.variables ?? {});
    if (res.status === 401 && (await tryRefresh())) {
      res = await postOnce(op, options.variables ?? {});
    }
    // Vẫn 401 sau khi thử refresh = phiên hết hạn / token không hợp lệ (vd đổi
    // backend staging↔prod). Xoá token + đưa về trang login để không kẹt ở màn
    // hình "unauthorized". Bỏ qua nếu đã ở /login (tránh vòng lặp redirect).
    if (res.status === 401 && typeof window !== 'undefined') {
      tokenStore.clear();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    const body = res.status === 204 ? null : await res.json().catch(() => null);
    if (!res.ok) {
      return { data: undefined, errors: [{ message: body?.error || `HTTP ${res.status}` }] };
    }
    return { data: { [op]: body } as unknown as T };
  },
};

export const getApiClient = () => restClient;
